import React, { useState, useEffect, useCallback } from 'react';
import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { marinaAPI } from '@/lib/api';
import { 
  Anchor, Fuel, Utensils, Wrench, ShoppingBag, 
  Phone, Globe, Radio, Clock, Star, MapPin,
  ChevronRight, X, Loader2, Filter
} from 'lucide-react';

// Business type icons and colors
const BUSINESS_ICONS = {
  marina: { icon: Anchor, color: '#3b82f6', label: 'Marina' },
  fuel_dock: { icon: Fuel, color: '#ef4444', label: 'Fuel Dock' },
  restaurant: { icon: Utensils, color: '#f59e0b', label: 'Restaurant' },
  boat_ramp: { icon: MapPin, color: '#10b981', label: 'Boat Ramp' },
  pump_out: { icon: Anchor, color: '#8b5cf6', label: 'Pump Out' },
  repair: { icon: Wrench, color: '#6b7280', label: 'Repair' },
  supplies: { icon: ShoppingBag, color: '#ec4899', label: 'Supplies' },
  other: { icon: MapPin, color: '#6b7280', label: 'Other' },
};

// Create marker icon for each business type
const createBusinessIcon = (type, isFeatured = false) => {
  const config = BUSINESS_ICONS[type] || BUSINESS_ICONS.other;
  const size = isFeatured ? 36 : 28;
  const borderWidth = isFeatured ? 3 : 2;
  
  return L.divIcon({
    className: 'custom-business-marker',
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background: ${config.color};
        border: ${borderWidth}px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        ${isFeatured ? 'animation: pulse 2s infinite;' : ''}
      ">
        <span style="color: white; font-size: ${isFeatured ? '16px' : '12px'};">
          ${type === 'marina' ? '⚓' : type === 'fuel_dock' ? '⛽' : type === 'restaurant' ? '🍽️' : '📍'}
        </span>
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size/2, size/2],
    popupAnchor: [0, -size/2],
  });
};

const MarinaMarkers = ({ marinas, onTap }) => {
  return (
    <>
      {marinas.map((marina) => (
        <Marker
          key={marina.id}
          position={[marina.lat, marina.lon]}
          icon={createBusinessIcon(marina.business_type, marina.is_featured)}
        >
          <Popup>
            <div className="min-w-[200px] max-w-[280px]">
              {/* Header */}
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <h3 className="font-bold text-sm leading-tight">{marina.name}</h3>
                  <Badge variant="outline" className="text-xs mt-1">
                    {BUSINESS_ICONS[marina.business_type]?.label || 'Business'}
                  </Badge>
                </div>
                {marina.is_featured && (
                  <Badge className="bg-yellow-500 text-white text-xs">
                    <Star className="w-3 h-3 mr-1" />
                    Featured
                  </Badge>
                )}
              </div>
              
              {/* Distance */}
              {marina.distance_nm !== undefined && (
                <p className="text-xs text-gray-500 mb-2">
                  📍 {marina.distance_nm} nm away
                </p>
              )}
              
              {/* Promotion (Featured only) */}
              {marina.promotion && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mb-2">
                  <p className="text-xs text-yellow-800">🎉 {marina.promotion}</p>
                </div>
              )}
              
              {/* Fuel info */}
              {marina.fuel_info && (marina.fuel_info.has_gas || marina.fuel_info.has_diesel) && (
                <div className="flex gap-2 mb-2">
                  {marina.fuel_info.has_gas && (
                    <Badge variant="outline" className="text-xs">
                      ⛽ Gas {marina.fuel_info.gas_price ? `$${marina.fuel_info.gas_price}` : ''}
                    </Badge>
                  )}
                  {marina.fuel_info.has_diesel && (
                    <Badge variant="outline" className="text-xs">
                      🛢️ Diesel {marina.fuel_info.diesel_price ? `$${marina.fuel_info.diesel_price}` : ''}
                    </Badge>
                  )}
                </div>
              )}
              
              {/* Services */}
              {marina.services && marina.services.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {marina.services.slice(0, 4).map((service, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      {service}
                    </Badge>
                  ))}
                  {marina.services.length > 4 && (
                    <Badge variant="secondary" className="text-xs">
                      +{marina.services.length - 4} more
                    </Badge>
                  )}
                </div>
              )}
              
              {/* Contact buttons */}
              <div className="flex gap-2 mt-2">
                {marina.phone && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 h-8 text-xs"
                    onClick={() => {
                      onTap(marina.id);
                      window.open(`tel:${marina.phone}`, '_self');
                    }}
                  >
                    <Phone className="w-3 h-3 mr-1" />
                    Call
                  </Button>
                )}
                {marina.website && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 h-8 text-xs"
                    onClick={() => {
                      onTap(marina.id);
                      window.open(marina.website, '_blank');
                    }}
                  >
                    <Globe className="w-3 h-3 mr-1" />
                    Website
                  </Button>
                )}
              </div>
              
              {/* VHF Channel */}
              {marina.vhf_channel && (
                <p className="text-xs text-gray-500 mt-2 flex items-center">
                  <Radio className="w-3 h-3 mr-1" />
                  VHF Ch. {marina.vhf_channel}
                </p>
              )}
            </div>
          </Popup>
        </Marker>
      ))}
    </>
  );
};

const MarinaPanel = ({ lat, lon, isVisible, onClose }) => {
  const [marinas, setMarinas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(null);
  
  const fetchMarinas = useCallback(async () => {
    if (!lat || !lon) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await marinaAPI.getNearby(lat, lon, 25, filter);
      setMarinas(response.data.listings || []);
    } catch (err) {
      console.error('Marina fetch error:', err);
      setError('Unable to load nearby businesses');
    } finally {
      setLoading(false);
    }
  }, [lat, lon, filter]);
  
  useEffect(() => {
    if (isVisible) {
      fetchMarinas();
    }
  }, [isVisible, fetchMarinas]);
  
  const handleTap = async (listingId) => {
    try {
      await marinaAPI.recordTap(listingId);
    } catch (err) {
      // Silently fail - analytics shouldn't block user
    }
  };
  
  if (!isVisible) return null;
  
  return (
    <>
      {/* Marina markers on map */}
      <MarinaMarkers marinas={marinas} onTap={handleTap} />
      
      {/* Side panel (optional - can be toggled) */}
      <Card className="absolute top-20 right-4 z-[1000] bg-white/95 backdrop-blur shadow-lg w-72 max-h-[60vh] overflow-hidden" data-testid="marina-panel">
        <CardContent className="p-0">
          {/* Header */}
          <div className="p-3 border-b bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Anchor className="w-5 h-5" />
                <span className="font-semibold">Nearby</span>
              </div>
              <Button 
                size="sm" 
                variant="ghost" 
                className="text-white hover:bg-white/20 h-7 w-7 p-0"
                onClick={onClose}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Filter buttons */}
          <div className="p-2 border-b flex gap-1 overflow-x-auto">
            <Button 
              size="sm" 
              variant={filter === null ? "default" : "ghost"}
              className="h-7 text-xs whitespace-nowrap"
              onClick={() => setFilter(null)}
            >
              All
            </Button>
            <Button 
              size="sm" 
              variant={filter === "fuel_dock" ? "default" : "ghost"}
              className="h-7 text-xs whitespace-nowrap"
              onClick={() => setFilter("fuel_dock")}
            >
              ⛽ Fuel
            </Button>
            <Button 
              size="sm" 
              variant={filter === "marina" ? "default" : "ghost"}
              className="h-7 text-xs whitespace-nowrap"
              onClick={() => setFilter("marina")}
            >
              ⚓ Marinas
            </Button>
            <Button 
              size="sm" 
              variant={filter === "restaurant" ? "default" : "ghost"}
              className="h-7 text-xs whitespace-nowrap"
              onClick={() => setFilter("restaurant")}
            >
              🍽️ Food
            </Button>
          </div>
          
          {/* Content */}
          <div className="overflow-y-auto max-h-[40vh]">
            {loading ? (
              <div className="p-4 text-center">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-600" />
                <p className="text-sm text-gray-500 mt-2">Loading...</p>
              </div>
            ) : error ? (
              <div className="p-4 text-center text-red-600 text-sm">
                {error}
              </div>
            ) : marinas.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                <Anchor className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No businesses found nearby</p>
                <p className="text-xs mt-1">Try expanding your search area</p>
              </div>
            ) : (
              <div className="divide-y">
                {marinas.map((marina) => (
                  <div 
                    key={marina.id} 
                    className={`p-3 hover:bg-gray-50 cursor-pointer ${marina.is_featured ? 'bg-yellow-50' : ''}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          {marina.is_featured && <Star className="w-3 h-3 text-yellow-500" />}
                          <h4 className="font-medium text-sm truncate">{marina.name}</h4>
                        </div>
                        <p className="text-xs text-gray-500">
                          {BUSINESS_ICONS[marina.business_type]?.label} • {marina.distance_nm} nm
                        </p>
                        {marina.promotion && (
                          <p className="text-xs text-yellow-600 mt-1">🎉 {marina.promotion}</p>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Footer */}
          {marinas.length > 0 && (
            <div className="p-2 border-t text-center">
              <p className="text-xs text-gray-400">
                {marinas.length} businesses within 25 nm
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default MarinaPanel;
